import React, { useEffect, useRef, useState } from 'react';
import { getQuizDocEditorConfig } from './quizDocumentsApi';
import useOnlyOfficeScript from './useOnlyOfficeScript';

interface QuizDocumentEditorProps {
  docId: number;
  /** 'edit' for admin authoring, 'view' for read-only display */
  mode?: 'edit' | 'view';
  /** 'desktop' full editor UI, 'embedded' compact viewer (good for narrow panels) */
  editorType?: 'desktop' | 'embedded';
  height?: string;
  onDocumentReady?: () => void;
}

let editorInstanceCounter = 0;

/**
 * Mounts an OnlyOffice DocsAPI editor for a quiz explanation document.
 * Antd-free so it can be used both in QuizManagement and in the OHIF
 * questionnaire panel.
 */
const QuizDocumentEditor: React.FC<QuizDocumentEditorProps> = ({
  docId,
  mode = 'edit',
  editorType = 'desktop',
  height = '100%',
  onDocumentReady,
}) => {
  const [containerId] = useState(() => `quiz-doc-editor-${++editorInstanceCounter}`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [config, setConfig] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);
  const editorRef = useRef<any>(null);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // Fetch the signed editor config from the backend
  useEffect(() => {
    if (!docId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setConfig(null);

    getQuizDocEditorConfig(docId, { mode, type: editorType })
      .then(res => {
        if (cancelled) return;
        const { config: editorConfig, onlyOfficeUrl: url } = res.data.data;
        setConfig(editorConfig);
        setOnlyOfficeUrl(url);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load editor configuration');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [docId, mode, editorType, retryCount]);

  // Initialize the editor once script + config are ready
  useEffect(() => {
    if (!scriptLoaded || !config) return;

    const timer = setTimeout(() => {
      try {
        editorRef.current = new (window as any).DocsAPI.DocEditor(containerId, {
          ...config,
          width: '100%',
          height: '100%',
          events: {
            onDocumentReady: () => {
              setLoading(false);
              onDocumentReady?.();
            },
            onError: (event: any) => {
              console.error('OnlyOffice editor error:', event);
            },
          },
        });
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setError('Failed to initialize editor');
        setLoading(false);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (editorRef.current) {
        try {
          editorRef.current.destroyEditor();
        } catch (e) {
          // already destroyed
        }
        editorRef.current = null;
      }
    };
  }, [scriptLoaded, config, containerId]);

  const handleRetry = () => {
    if (scriptError) retryScript();
    setRetryCount(c => c + 1);
  };

  if (scriptError || error) {
    return (
      <div
        style={{ height }}
        className="flex flex-col items-center justify-center gap-3 rounded border border-red-500/40 bg-red-500/10 p-4 text-center"
      >
        <span className="text-sm text-red-400">{scriptError || error}</span>
        <button
          onClick={handleRetry}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ height, position: 'relative', width: '100%' }}>
      {loading && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.25)' }}
        >
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
        </div>
      )}
      <div style={{ height: '100%', width: '100%' }}>
        <div id={containerId} />
      </div>
    </div>
  );
};

export default QuizDocumentEditor;
